import { useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router';
import { cx } from '@/lib/format';
import { PathIcon, P } from './Icon';

interface ToastItem {
  id: number;
  kind: 'info' | 'error' | 'note';
  title: string;
  body?: string;
  link?: string | null;
  /** A call to action under the text ("Try it on"). */
  action?: string;
  /** A picture on the left: the Legendary outfit's first frame (it has the figure panel's background). */
  image?: string;
}

let items: ToastItem[] = [];
let seq = 0;
const subs = new Set<() => void>();
const publish = () => subs.forEach((s) => s());

function push(t: Omit<ToastItem, 'id'>, ms = 4200) {
  const id = ++seq;
  items = [...items.slice(-3), { ...t, id }];
  publish();
  setTimeout(() => {
    items = items.filter((x) => x.id !== id);
    publish();
  }, ms);
}

export const toast = {
  info: (title: string) => push({ kind: 'info', title }),
  error: (title: string) => push({ kind: 'error', title }, 5200),
  /** `action`: a button line under the text (krug 5, K8: toasts that are not in the list). */
  note: (title: string, body?: string, link?: string | null, action?: string) => push({ kind: 'note', title, body, link, action }, 6500),
  /** New outfit (docs/OUTFITI_garderoba.md, 4.5); `image` makes it the bigger Legendary one. */
  outfit: (title: string, link: string | null, action: string, image?: string) => push({ kind: 'note', title, link, action, image }, image ? 9000 : 6500),
};

export function Toaster() {
  const list = useSyncExternalStore(
    (l) => {
      subs.add(l);
      return () => subs.delete(l);
    },
    () => items,
  );
  const navigate = useNavigate();
  return (
    <div className="pointer-events-none fixed inset-x-0 top-3 z-[100] flex flex-col items-center gap-2 px-4" aria-live="polite">
      {list.map((t) => (
        <button
          key={t.id}
          onClick={() => {
            if (t.link) navigate(t.link);
            items = items.filter((x) => x.id !== t.id);
            publish();
          }}
          className={cx(
            'pointer-events-auto flex w-full max-w-[420px] animate-rise flex-col gap-0.5 rounded-[6px] border px-4 py-3 text-left shadow-pop',
            t.kind === 'error' ? 'border-line bg-raised' : t.kind === 'note' ? 'border-line bg-surface' : 'border-border bg-raised',
          )}
        >
          <span className={cx('flex items-center gap-3', t.image && 'min-h-24')}>
            {t.image && (
              <span className="h-24 w-16 shrink-0 overflow-hidden rounded-[4px] border border-legendary">
                <img src={t.image} alt="" className="h-full w-full object-cover" />
              </span>
            )}
            <span className="flex min-w-0 flex-col gap-0.5">
              {/* A failed action: red text with the warning icon (1. 10. 2026). */}
              <span className={cx('font-semibold', t.image ? 'text-[16px] text-legendary' : 'text-[14px]', t.kind === 'error' ? 'flex items-start gap-1.5 text-danger-text' : !t.image && 'text-text')}>
                {t.kind === 'error' && <PathIcon d={P.warn} size={16} className="mt-0.5 shrink-0" />}
                {t.title}
              </span>
              {t.body && <span className="text-[13px] leading-[18px] text-muted">{t.body}</span>}
              {t.action && <span className="text-[13px] font-semibold text-text">{t.action} →</span>}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}
